import React, { memo, useCallback, useMemo, useState } from 'react';

import { AutocompleteCDS } from '@sharedCR/coreDesignSystem/form';
import { crmContactApi, realtorsApi } from 'app/services/api';
import PropTypes from 'prop-types';

import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { PropertyAndLoanTestIds } from '../../../../../PropertyAndLoan.testIds';
import { findAgentTagId } from '../AgentAndSeller.utils';

import RealtorPickerOption from './components/RealtorPickerOption';
import { getCrmAgentOptions, getRealtorOptions, mergeRealtorOptions } from './RealtorPicker.utils';

const realtorParams = { includes: ['assignedloanofficer'] };
const CRM_AGENT_PAGE_SIZE = 100;

const RealtorPicker = ({ onSelect, disabled = false }) => {
  const companyId = useCompanyId();

  const [realtors, setRealtors] = useState([]);
  const [crmAgentContacts, setCrmAgentContacts] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  const options = useMemo(() => {
    const directoryOptions = getRealtorOptions(realtors);
    const crmOptions = getCrmAgentOptions(crmAgentContacts);

    return mergeRealtorOptions(directoryOptions, crmOptions);
  }, [realtors, crmAgentContacts]);

  const isOptionEqualToValue = useCallback((option, { value }) => option.value === value, []);

  const onChange = useCallback(
    (e, option) => {
      setSelectedOption(option);

      if (option?.realtor) {
        onSelect(option.realtor);
      }
    },
    [onSelect],
  );

  // Fetch the active company's realtor directory into local state so the picker
  // is scoped per-loan-company (the shared realtors store merges across
  // companies) and a directory-fetch failure degrades silently to no options.
  useEffectSafeStrictMode(() => {
    const lifecycle = { cancelled: false };

    const loadRealtors = async () => {
      try {
        const { data } = await realtorsApi.getRealtors(realtorParams);

        if (!lifecycle.cancelled) {
          setRealtors(data ?? []);
        }
      } catch {
        if (!lifecycle.cancelled) {
          setRealtors([]);
        }
      }
    };

    loadRealtors();

    return () => {
      lifecycle.cancelled = true;
    };
  }, [companyId]);

  // Fetch CRM agent-contacts tagged "Realtor Agent" (read-only: skip when the
  // tag doesn't exist yet so we don't create it on render).
  useEffectSafeStrictMode(() => {
    const lifecycle = { cancelled: false };

    const loadCrmAgents = async () => {
      try {
        const tagId = await findAgentTagId();

        if (!tagId || lifecycle.cancelled) {
          return;
        }

        const { data } = await crmContactApi.getContacts({
          tagId,
          pageSize: CRM_AGENT_PAGE_SIZE,
        });

        const items = Array.isArray(data) ? data : (data?.items ?? []);

        if (!lifecycle.cancelled) {
          setCrmAgentContacts(items);
        }
      } catch {
        if (!lifecycle.cancelled) {
          setCrmAgentContacts([]);
        }
      }
    };

    loadCrmAgents();

    return () => {
      lifecycle.cancelled = true;
    };
  }, [companyId]);

  return (
    <AutocompleteCDS
      OptionContent={RealtorPickerOption}
      dataTestId={PropertyAndLoanTestIds.REALTOR_PICKER}
      dataTestIdOption={PropertyAndLoanTestIds.REALTOR_PICKER_OPTION}
      disabled={disabled}
      isOptionEqualToValue={isOptionEqualToValue}
      label="Find a saved realtor"
      name="savedRealtor"
      options={options}
      placeholder="Search by name or email"
      value={selectedOption}
      onChange={onChange}
    />
  );
};

RealtorPicker.propTypes = {
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default memo(RealtorPicker);
